import {
  Dialog as BaseDialog,
  type DialogRootProps,
  Portal,
} from "@ark-ui/react";
import { X } from "@phosphor-icons/react";
import clsx from "clsx";
import * as React from "react";
import { Button, type ButtonProps } from "../button/Button.js";
import { IconButton, type IconButtonProps } from "../icon-button/IconButton.js";
import { ScrollArea, type ScrollAreaProps } from "../scroll-area/ScrollArea.js";
import { useThemeContext } from "../theme/Theme.js";

interface DialogProps extends DialogRootProps {
  scroll?: "body" | "modal";
  modalScrollAreaProps?: Omit<ScrollAreaProps, "children">;
  title?: string | React.ReactNode;
  icon?: React.ReactNode;
  description?: React.ReactNode;
  children: React.ReactNode;
  size?: "sm" | "md" | "lg" | "xl";
  withCloseButton?: boolean;
  closeButtonProps?: Pick<IconButtonProps, "variant" | "color" | "size">;
  padding?: "none" | "sm" | "md" | "lg";
  classNames?: {
    backdrop?: string;
    content?: string;
    title?: string;
    positioner?: string;
    description?: string;
    closeTrigger?: string;
    header?: string;
    footer?: string;
    main?: string;
  };
}

interface WithoutDialogFooterProps extends DialogProps {
  withFooter?: false;
}

interface WithDialogFooterProps extends DialogProps {
  withFooter?: true;
  labels?: {
    confirm?: string;
    cancel?: string;
  };
  onCancel?: React.MouseEventHandler<HTMLButtonElement>;
  onConfirm?: React.MouseEventHandler<HTMLButtonElement>;
  cancelButtonProps?: Pick<ButtonProps, "variant" | "color" | "size">;
  confirmButtonProps?: Pick<ButtonProps, "variant" | "color" | "size">;
}

type DialogComponentProps = WithDialogFooterProps | WithoutDialogFooterProps;

const useScrollWithShadow = () => {
  const [scrollData, setScrollData] = React.useState({
    scrollTop: 0,
    scrollHeight: 0,
    clientHeight: 0,
  });

  // Return custom scrollhandler function to keep a track of the scroll status
  const onScrollHandler = React.useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
      setScrollData({ scrollTop, scrollHeight, clientHeight });
    },
    [],
  );

  const { scrollTop, scrollHeight, clientHeight } = scrollData;
  // Calculate different scroll states and return them
  const isBottom = React.useMemo(
    () => clientHeight === scrollHeight - scrollTop,
    [scrollTop, scrollHeight, clientHeight],
  );
  const isTop = React.useMemo(() => scrollTop === 0, [scrollTop]);
  const isBetween = React.useMemo(
    () => scrollTop > 0 && clientHeight < scrollHeight - scrollTop,
    [scrollTop, scrollHeight, clientHeight],
  );
  const hasScroll = React.useMemo(
    () => scrollTop > 0 || clientHeight < scrollHeight - scrollTop,
    [scrollTop, scrollHeight, clientHeight],
  );

  return { isBottom, isTop, isBetween, hasScroll, onScrollHandler };
};

const Dialog = (props: DialogComponentProps) => {
  const { isBottom, isTop, isBetween, hasScroll, onScrollHandler } =
    useScrollWithShadow();

  const {
    title,
    description,
    children,
    size = "sm",
    icon,
    withFooter = false,
    withCloseButton = true,
    classNames,
    padding = "md",
    scroll = "modal",
    modalScrollAreaProps,
    closeButtonProps = {
      variant: "tertiary",
      size: "xs",
      color: "gray",
    },
    ...rootProps
  } = props;

  const { primaryColor, secondaryColor } = useThemeContext();

  let footerProps: Partial<WithDialogFooterProps> = {};

  if (withFooter) {
    const {
      labels,
      onCancel,
      onConfirm,
      cancelButtonProps = {
        variant: "tertiary",
        color: "gray",
        size: "md",
      },
      confirmButtonProps = {
        variant: "primary",
        color: "primary",
        size: "md",
      },
    } = props as WithDialogFooterProps;

    footerProps = {
      labels,
      onCancel,
      onConfirm,
      cancelButtonProps,
      confirmButtonProps,
    };
  }

  const handleConfirm = React.useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      if (footerProps.onConfirm) {
        footerProps.onConfirm(event);
      }
    },
    [footerProps.onConfirm],
  );

  const handleCancel = React.useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      if (footerProps.onCancel) {
        footerProps.onCancel(event);
      }
    },
    [footerProps.onCancel],
  );

  return (
    <BaseDialog.Root lazyMount unmountOnExit {...rootProps}>
      <Portal>
        <BaseDialog.Backdrop
          data-primary-color={primaryColor}
          data-secondary-color={secondaryColor}
          className={clsx("ox-reset ox-DialogBackdrop", classNames?.backdrop)}
        />

        <BaseDialog.Positioner
          data-primary-color={primaryColor}
          data-secondary-color={secondaryColor}
          className={clsx(
            "ox-reset ox-DialogPositioner",
            classNames?.positioner,
          )}
        >
          <BaseDialog.Content
            data-size={size}
            data-padding={padding}
            className={clsx("ox-reset ox-DialogContent", classNames?.content)}
          >
            {withCloseButton ? (
              <BaseDialog.CloseTrigger
                className={clsx(
                  "ox-reset ox-DialogCloseTrigger",
                  classNames?.closeTrigger,
                )}
                asChild
              >
                {/*@ts-ignore */}
                <IconButton {...closeButtonProps}>
                  <X />
                </IconButton>
              </BaseDialog.CloseTrigger>
            ) : null}
            {title || description ? (
              <header
                className={clsx(
                  "ox-reset ox-DialogHeader",
                  title && icon ? "ox-d-icon" : "",
                  classNames?.header,
                )}
                data-scroll={
                  hasScroll && (isBetween || isBottom) ? true : false
                }
              >
                {title && icon ? icon : null}
                {typeof title === "string" ? (
                  <BaseDialog.Title
                    className={clsx(
                      "ox-reset ox-DialogTitle",
                      "ox-fz-lg",
                      classNames?.title,
                    )}
                  >
                    {title}
                  </BaseDialog.Title>
                ) : (
                  title
                )}

                {description ? (
                  <BaseDialog.Description
                    className={clsx(
                      "ox-reset ox-DialogDescription",
                      classNames?.description,
                    )}
                  >
                    {description}
                  </BaseDialog.Description>
                ) : null}
              </header>
            ) : null}

            <ScrollArea
              viewportProps={{
                onScroll: onScrollHandler,
              }}
              {...modalScrollAreaProps}
              className={clsx(
                "ox-reset ox-DialogMain",
                scroll === "modal" ? "ox-d-scroll-modal" : "",
                classNames?.main,
              )}
            >
              {children}
            </ScrollArea>

            {withFooter ? (
              <footer
                className={clsx("ox-reset ox-DialogFooter", classNames?.footer)}
                data-scroll={hasScroll && (isBetween || isTop) ? true : false}
              >
                {/*@ts-ignore */}
                <Button
                  onClick={handleCancel}
                  {...footerProps.cancelButtonProps}
                >
                  {footerProps.labels?.cancel || "Cancel"}
                </Button>
                {/*@ts-ignore */}
                <Button
                  onClick={handleConfirm}
                  {...footerProps.confirmButtonProps}
                >
                  {footerProps.labels?.confirm || "Confirm"}
                </Button>
              </footer>
            ) : null}
          </BaseDialog.Content>
        </BaseDialog.Positioner>
      </Portal>
    </BaseDialog.Root>
  );
};

Dialog.displayName = "Dialog";
export { Dialog };
export type { DialogComponentProps };
